import styled from 'styled-components'
import { vimovaTheme } from '../../theme/vimovaTheme'

export const TwoColumnTop = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  align-items: stretch;

  @media print {
    display: flex !important;
    gap: 16px !important;
    margin-top: 12px !important;
    align-items: stretch !important;
  }
`

export const PhotoCard = styled.div<{ $imageUrl?: string }>`
  width: 270px;
  min-height: 280px;
  border-radius: ${vimovaTheme.borderRadius.signature};
  border: 1px solid ${vimovaTheme.colors.border};
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center/cover no-repeat`
      : vimovaTheme.colors.backgroundMuted};

  @media print {
    width: 248px !important;
    min-height: 280px !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
    border: 1px solid
      ${({ $imageUrl }) =>
        $imageUrl ? vimovaTheme.colors.border : 'transparent'} !important;
    background: ${({ $imageUrl }) =>
      $imageUrl
        ? `url(${$imageUrl}) center/cover no-repeat`
        : 'transparent'} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PhotoGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 68px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);

  @media print {
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.6),
      transparent
    ) !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PhotoPlaceholderText = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: rgba(255, 255, 255, 0.4);
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.medium};

  /* Hidden in the PDF — an absent cover photo stays blank, no label. */
  @media print {
    display: none !important;
  }
`

export const PhotoEditOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  font-family: ${vimovaTheme.fonts.heading};
  font-size: ${vimovaTheme.fontSizes.body};
  font-weight: ${vimovaTheme.fontWeights.bold};
  color: ${vimovaTheme.colors.white};
  border-radius: ${vimovaTheme.borderRadius.signature};
  z-index: 3;

  *:hover > & {
    opacity: 1;
  }

  @media print {
    display: none !important;
  }
`

export const AddressOverlay = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1;

  @media print {
    position: absolute !important;
    bottom: 12px !important;
    left: 12px !important;
  }
`

export const AddressMain = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: ${vimovaTheme.fontSizes.small};
  color: ${vimovaTheme.colors.white};

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: ${vimovaTheme.fontSizes.small} !important;
    color: ${vimovaTheme.colors.white} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const AddressSub = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.micro};
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;

  @media print {
    font-size: ${vimovaTheme.fontSizes.micro} !important;
    color: rgba(255, 255, 255, 0.8) !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CharacteristicsCard = styled.div`
  flex: 1;
  background: ${vimovaTheme.colors.white};
  border-radius: ${vimovaTheme.borderRadius.signature};
  border: 1px solid ${vimovaTheme.colors.border};
  padding: 16px 20px;
  box-sizing: border-box;

  @media print {
    flex: 1 !important;
    background: ${vimovaTheme.colors.white} !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
    border: 1px solid ${vimovaTheme.colors.border} !important;
    padding: 16px 20px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CharacteristicsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  @media print {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
    margin-bottom: 12px !important;
  }
`

export const CharacteristicsTitle = styled.div`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 13px;
  color: ${vimovaTheme.colors.textDark};

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 13px !important;
    color: ${vimovaTheme.colors.textDark} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CharRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;

  & + & {
    border-top: 1px solid ${vimovaTheme.colors.border};
  }

  @media print {
    display: flex !important;
    justify-content: space-between !important;
    padding: 6px 0 !important;

    & + & {
      border-top: 1px solid ${vimovaTheme.colors.border} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const CharLabel = styled.span`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  color: ${vimovaTheme.colors.textLight};

  @media print {
    font-size: 10px !important;
    color: ${vimovaTheme.colors.textLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const CharValue = styled.span`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  color: ${vimovaTheme.colors.textLight};

  @media print {
    font-size: 10px !important;
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    color: ${vimovaTheme.colors.textLight} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const EnergyBadge = styled.span<{ $class?: string }>`
  display: inline-block;
  background: ${({ $class }) => {
    switch ($class) {
      case 'A':
        return '#2d8c3c'
      case 'B':
        return '#5ba52e'
      case 'C':
        return '#a5cc74'
      case 'D':
        return '#ef713f'
      case 'E':
        return '#e85d2c'
      case 'F':
        return '#dc3545'
      case 'G':
        return '#b71c1c'
      default:
        return vimovaTheme.colors.textMuted
    }
  }};
  color: ${vimovaTheme.colors.white};
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  padding: 2px 8px;
  border-radius: 3px;
  text-align: center;

  @media print {
    background: ${({ $class }) => {
      switch ($class) {
        case 'A':
          return '#2d8c3c'
        case 'B':
          return '#5ba52e'
        case 'C':
          return '#a5cc74'
        case 'D':
          return '#ef713f'
        case 'E':
          return '#e85d2c'
        case 'F':
          return '#dc3545'
        case 'G':
          return '#b71c1c'
        default:
          return vimovaTheme.colors.textMuted
      }
    }} !important;
    color: ${vimovaTheme.colors.white} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const EquipmentSection = styled.div`
  margin-top: 12px;

  @media print {
    margin-top: 12px !important;
  }
`

export const EquipmentTitle = styled.div`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: 12px;
  color: ${vimovaTheme.colors.primary};
  margin-bottom: 10px;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: 12px !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media print {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 10px !important;
  }
`

export const FeatureTag = styled.span`
  display: inline-block;
  background: rgba(165, 204, 116, 0.15);
  color: ${vimovaTheme.colors.greenDark};
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  font-weight: ${vimovaTheme.fontWeights.bold};
  padding: 6px 16px;
  border-radius: 13px;

  @media print {
    background: rgba(165, 204, 116, 0.15) !important;
    color: ${vimovaTheme.colors.greenDark} !important;
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DescriptionSection = styled.div`
  margin-top: 20px;

  @media print {
    margin-top: 20px !important;
  }
`

export const DescriptionTitle = styled.h3`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: ${vimovaTheme.fontSizes.body};
  color: ${vimovaTheme.colors.primary};
  margin: 0 0 6px 0;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: ${vimovaTheme.fontSizes.body} !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const DescriptionText = styled.div`
  font-family: ${vimovaTheme.fonts.body};
  font-size: ${vimovaTheme.fontSizes.small};
  color: ${vimovaTheme.colors.text};
  line-height: 1.6;
  margin: 0;

  p {
    margin: 0;
  }

  strong {
    font-weight: ${vimovaTheme.fontWeights.bold};
  }

  em {
    font-style: italic;
  }

  @media print {
    font-size: ${vimovaTheme.fontSizes.small} !important;
    color: ${vimovaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PointsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 20px;

  @media print {
    display: flex !important;
    gap: 16px !important;
    margin-top: 20px !important;
  }
`

export const PointsCard = styled.div<{ $variant: 'strengths' | 'weaknesses' }>`
  flex: 1;
  background: ${vimovaTheme.colors.white};
  border-radius: ${vimovaTheme.borderRadius.signature};
  border: 1px solid ${vimovaTheme.colors.border};
  padding: 12px 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${({ $variant }) =>
      $variant === 'strengths'
        ? vimovaTheme.colors.green
        : vimovaTheme.colors.orange};
  }

  @media print {
    flex: 1 !important;
    background: ${vimovaTheme.colors.white} !important;
    border-radius: ${vimovaTheme.borderRadius.signature} !important;
    border: 1px solid ${vimovaTheme.colors.border} !important;
    padding: 12px 16px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;

    &::before {
      background: ${({ $variant }) =>
        $variant === 'strengths'
          ? vimovaTheme.colors.green
          : vimovaTheme.colors.orange} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const PointsTitle = styled.div`
  font-family: ${vimovaTheme.fonts.heading};
  font-weight: ${vimovaTheme.fontWeights.bold};
  font-size: ${vimovaTheme.fontSizes.body};
  color: ${vimovaTheme.colors.primary};
  margin-bottom: 8px;

  @media print {
    font-weight: ${vimovaTheme.fontWeights.bold} !important;
    font-size: ${vimovaTheme.fontSizes.body} !important;
    color: ${vimovaTheme.colors.primary} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const PointItem = styled.div<{ $variant: 'strengths' | 'weaknesses' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-top: 4px;
    flex-shrink: 0;
    background: ${({ $variant }) =>
      $variant === 'strengths'
        ? vimovaTheme.colors.green
        : vimovaTheme.colors.orange};
  }

  @media print {
    display: flex !important;
    gap: 8px !important;
    margin-bottom: 12px !important;

    &::before {
      background: ${({ $variant }) =>
        $variant === 'strengths'
          ? vimovaTheme.colors.green
          : vimovaTheme.colors.orange} !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`

export const PointText = styled.span`
  font-family: ${vimovaTheme.fonts.body};
  font-size: 10px;
  color: ${vimovaTheme.colors.text};
  line-height: 1.5;

  @media print {
    font-size: 10px !important;
    color: ${vimovaTheme.colors.text} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
